import { useId, useState } from 'react';
import { publicRegistry } from '@/i18n/dictionaries/publicRegistry';
import { useTranslation } from '@/i18n/useTranslation';
import { libraryApi } from '@/services/libraryApi';
import { CITATION_STYLES, citationStyleLabel } from '@/features/common/citationStyle';
import type { CitationButtonProps } from '@/features/common/types/CitationButtonProps';
import type { CitationResponse } from '@/features/common/types/CitationResponse';
import '@/features/common/citation.css';

/**
 * "Citează" on a library resource (FR-021).
 *
 * The tender names three things a reader can do with a resource — download, save and cite — and
 * the card offered one. The formatter was not missing: CitationService produces both ISO 690 and
 * APA and the endpoint answers correctly. Nothing in the interface called it, so the only way to
 * reach a working feature was to type the API URL by hand.
 *
 * Both styles are fetched at once rather than behind a style picker: a researcher generally knows
 * which one their journal wants, and showing both costs one extra request against a service that
 * formats strings from metadata already in memory.
 */
export function CitationButton({ resourceId }: CitationButtonProps) {
  const { t } = useTranslation(publicRegistry);
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [citations, setCitations] = useState<CitationResponse[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  async function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    // Fetched once and kept: the citation of a published resource does not change while the
    // reader is looking at it, and re-requesting on every toggle would also re-count the access.
    if (citations || failed) return;
    try {
      setCitations(
        await Promise.all(CITATION_STYLES.map((style) => libraryApi.citation(resourceId, style))),
      );
    } catch {
      setFailed(true);
    }
  }

  async function copy(citation: string) {
    try {
      await navigator.clipboard.writeText(citation);
      setCopied(citation);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard access can be refused outright — an insecure origin, or a browser setting.
      // The citation stays on screen and selectable, which is the fallback that always works,
      // so there is nothing to report here.
    }
  }

  return (
    <>
      <button
        type="button"
        className="btn btn--ghost"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
      >
        {open ? t('libraryCiteHide') : t('libraryCite')}
      </button>

      {open && (
        <div className="citation" id={panelId}>
          <h4 className="citation__heading">{t('libraryCiteHeading')}</h4>
          {/* role=status so the result is announced when it arrives: the panel opens empty and
              fills in, which a screen-reader user would otherwise never learn about. */}
          <div role="status">
            {!citations && !failed && (
              <p className="citation__pending">{t('libraryCiteLoading')}</p>
            )}
            {failed && <p className="citation__error">{t('libraryCiteError')}</p>}
          </div>
          {citations?.map((c) => (
            <div className="citation__style" key={c.style}>
              <span className="citation__label">{citationStyleLabel(c.style)}</span>
              <p className="citation__text">{c.citation}</p>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => copy(c.citation)}
              >
                {copied === c.citation ? t('libraryCiteCopied') : t('libraryCiteCopy')}
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
